import { Box, useBreakpointValue } from '@chakra-ui/react';
import { AdvancedImage, responsive } from '@cloudinary/react';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { webp } from '@cloudinary/url-gen/qualifiers/format';
import { gridConfig } from '@time-line/components/common';
import type { SanityImage } from '@time-line/components/types';
import { cloudinary } from 'lib/cloudinary';
import { useImage } from 'lib/sanity';
import Img from 'next/image';

const { gridBorderRadius } = gridConfig;

export function BlurredHeroBackgroundImage({
  cloudinaryId,
  image,
}: {
  cloudinaryId?: string;
  image?: SanityImage;
}) {
  const aspectRatio = useBreakpointValue(
    {
      base: '9:16',
      md: '13:6',
    },
    {
      fallback: 'md',
    },
  );

  const cloudinaryImage = cloudinary
    .video(cloudinaryId)
    .resize(fill().aspectRatio(aspectRatio!))
    .delivery(format(webp()));

  const imageProps = useImage(image || null, {
    ratio: 1 / 1,
    fill: true,
  });

  return (
    <Box
      _after={{
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'blackAlpha.500',
        borderRadius: 'grid',
        '@supports (backdrop-filter: blur(100px))': {
          backdropFilter: 'auto',
          backdropBlur: '100px',
        },
      }}
    >
      <Box
        pos="absolute"
        inset={0}
        borderRadius={gridBorderRadius}
        overflow="hidden"
        background={image?.palette?.dominantBg || 'gray.100'}
        sx={{
          '@supports not (backdrop-filter: blur(100px))': {
            img: {
              display: 'none',
            },
          },
        }}
      >
        {image && imageProps && (
          <Img {...imageProps} role="presentation" alt="" />
        )}
        {cloudinaryId && cloudinaryImage && (
          <AdvancedImage
            style={{ height: '100%', width: '100%', objectFit: 'cover' }}
            role="presentation"
            alt=""
            cldImg={cloudinaryImage}
            plugins={[responsive({ steps: 200 })]}
          />
        )}
      </Box>
    </Box>
  );
}
